import { useEffect, useId, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon, IconButton } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState, Modal } from '@ds/feedback';
import { FormField, Input, Textarea } from '@ds/forms';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { dados } from '@/data/porta';
import type { DocumentoDaBase } from '@/data/tipos';

/**
 * A base de conhecimento do assistente.
 *
 * Os números a clínica já tem nas telas. O que os agentes não sabem é o que ela
 * **escreveu**: o
 * protocolo do procedimento, a política de remarcação, o que nunca prometer a
 * um paciente. Isso mora em PDF, em documento, na cabeça de quem trabalha há
 * anos — e é o que entra aqui.
 *
 * **O documento não vale no instante em que sobe.** Fatiar e vetorizar leva
 * segundos, do lado do servidor, e por isso cada linha carrega uma situação. Um
 * documento "pendente" que a tela mostrasse como pronto responderia o silêncio.
 *
 * **PDF digitalizado não vira base**, e a tela diz isso com todas as letras
 * quando acontece: imagem não tem texto para ler, e mandar tentar de novo com o
 * mesmo arquivo seria mandar repetir o erro.
 *
 * **Vetorizar exige chave OpenAI ou Gemini.** A chave que conversa pode ser a da
 * Anthropic, que não vetoriza. Sem uma das duas, a tela diz antes de a clínica
 * subir um arquivo — e não oferece um "Adicionar" que terminaria em falha.
 */
export function BaseDeConhecimento({ documentos }: { documentos: DocumentoDaBase[] }) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const idArquivo = useId();
    const [aberto, setAberto] = useState(false);
    const [titulo, setTitulo] = useState('');
    const [texto, setTexto] = useState('');
    const [arquivo, setArquivo] = useState<File | null>(null);
    const formSujo = useAlteracaoPendente({ titulo, texto, arquivo: arquivo?.name ?? null }, aberto);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [removendo, setRemovendo] = useState<DocumentoDaBase | null>(null);
    /* Nulo enquanto não se sabe — ou se a pergunta falhou: aí a tela não
       afirma nada, e a falha real aparece na preparação do documento. */
    const [vetoriza, setVetoriza] = useState<boolean | null>(null);
    /* A pergunta que falha ia só para o console: a tela não afirmava nada, e
       ninguém ficava sabendo que a verificação nem tinha respondido. */
    const [erroDaVerificacao, setErroDaVerificacao] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        dados().baseDeConhecimentoDisponivel()
            .then((d) => { if (vivo) { setVetoriza(d.disponivel); setErroDaVerificacao(null); } })
            .catch((e: unknown) => {
                if (!vivo) return;
                setVetoriza(null);
                setErroDaVerificacao(e instanceof Error ? e.message : 'O servidor não respondeu.');
            });
        return () => { vivo = false; };
    }, []);

    const semChaveDeEmbedding = vetoriza === false;

    const fechar = () => {
        setAberto(false);
        setTitulo('');
        setTexto('');
        setArquivo(null);
        setErro(null);
    };

    const salvar = async () => {
        const nome = titulo.trim();
        if (!nome) {
            setErro('Dê um título ao documento — é por ele que a resposta vai citar a fonte.');
            return;
        }
        if (!arquivo && texto.trim().length < 40) {
            setErro('Escreva ao menos alguns parágrafos, ou envie um arquivo.');
            return;
        }
        setSalvando(true);
        setErro(null);
        try {
            const doc = await dados().adicionarNaBase({
                titulo: nome,
                ...(arquivo ? { arquivo } : { texto }),
            });
            aviso(doc.situacao === 'pronto'
                ? { title: 'Documento na base', description: `${nome} · ${String(doc.trechos)} ${doc.trechos === 1 ? 'trecho' : 'trechos'}.` }
                : { title: 'Documento recebido', description: 'A preparação terminou com aviso — veja a linha na lista.' });
            fechar();
            void revalidate();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível adicionar o documento.');
        } finally {
            setSalvando(false);
        }
    };

    const reprocessar = async (doc: DocumentoDaBase) => {
        try {
            await dados().prepararDocumentoDaBase(doc.id);
            void revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível preparar', description: e instanceof Error ? e.message : '' });
        }
    };

    const remover = async () => {
        if (!removendo) return;
        try {
            await dados().removerDaBase(removendo.id);
            aviso({ title: 'Documento removido', description: `${removendo.titulo} saiu da base.` });
            setRemovendo(null);
            void revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : '' });
        }
    };

    const colunas: DataTableColumn<DocumentoDaBase>[] = [
        {
            key: 'titulo', header: 'Documento',
            render: (d) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Icon name={d.tipo === 'texto' ? 'file-text' : 'file'} size={15} />
                    <span style={{ minWidth: 0 }}>
                        <strong style={{ display: 'block', fontWeight: 600 }}>{d.titulo}</strong>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{dataEHoraPorExtenso(d.criadoEm)}</span>
                    </span>
                </span>
            ),
        },
        {
            key: 'situacao', header: 'Situação', width: 190,
            render: (d) => {
                if (d.situacao === 'pronto') {
                    /* A contagem de trechos é o que prova que o documento
                       responde: "pronto" com zero trecho não responderia nada. */
                    return <Badge tone="success">{d.trechos} {d.trechos === 1 ? 'trecho' : 'trechos'}</Badge>;
                }
                if (d.situacao === 'falhou') return <Badge tone="danger">Não virou base</Badge>;
                return <Badge tone="neutral">Preparando…</Badge>;
            },
        },
        {
            key: 'acoes', header: '', align: 'right', width: 96,
            render: (d) => (
                <span style={{ display: 'inline-flex', gap: 4 }}>
                    {d.situacao === 'falhou' && !semChaveDeEmbedding && (
                        <IconButton
                            size="sm" icon={<Icon name="refresh-cw" size={14} />}
                            label={`Preparar ${d.titulo} de novo`}
                            onClick={() => { void reprocessar(d); }}
                        />
                    )}
                    <IconButton
                        size="sm" icon={<Icon name="trash-2" size={14} />}
                        label={`Remover ${d.titulo}`}
                        onClick={() => setRemovendo(d)}
                    />
                </span>
            ),
        },
    ];

    const falhados = documentos.filter((d) => d.situacao === 'falhou');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Alert tone="info" dismissible={false} title="O que o assistente passa a saber">
                O que está escrito aqui é o que os números não mostram: protocolo, política de remarcação, o
                que nunca prometer. Os agentes consultam estes documentos quando a conversa tem a ver com eles,
                e citam de qual saiu a resposta.
            </Alert>

            {erroDaVerificacao && (
                <Alert tone="warning" dismissible={false} title="Não foi possível ver se a base consegue vetorizar">
                    {erroDaVerificacao} Documento novo ainda pode ser enviado; se a chave de IA não vetorizar, ele
                    aparece aqui marcado como falho, com o motivo. Recarregue a página para perguntar de novo.
                </Alert>
            )}

            {semChaveDeEmbedding && (
                <Alert tone="warning" dismissible={false} title="A chave de IA da plataforma não gera embeddings">
                    A base transforma o texto em vetores, e só OpenAI e Gemini fazem isso. A chave ativa conversa, mas
                    não vetoriza — até a Fórmula MED cadastrar uma chave OpenAI ou Gemini, documento novo não vira base e
                    o assistente responde só com os números da clínica.
                </Alert>
            )}

            {falhados.length > 0 && (
                /* O motivo fica em cima, inteiro. Dentro da linha ele seria
                   truncado, e um motivo truncado é um motivo que não ajuda. */
                <Alert tone="warning" dismissible={false} title={falhados.length === 1 ? 'Um documento não virou base' : `${String(falhados.length)} documentos não viraram base`}>
                    {falhados.map((d) => (
                        <p key={d.id} style={{ margin: '4px 0 0' }}>
                            <strong>{d.titulo}</strong>: {d.erro ?? 'a preparação falhou.'}
                        </p>
                    ))}
                </Alert>
            )}

            <Card
                title="Documentos"
                subtitle="PDF com texto, arquivo de texto, ou conteúdo digitado aqui mesmo."
                padding={0}
                actions={(
                    <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setAberto(true)} disabled={semChaveDeEmbedding}>
                        Adicionar
                    </Button>
                )}
            >
                {documentos.length === 0 ? (
                    <EmptyState
                        icon="book-open"
                        title="A base está vazia"
                        description="Sem documentos, o assistente responde só com os números da clínica. Comece pela política de remarcação ou pelo protocolo mais perguntado."
                        action={<Button onClick={() => setAberto(true)} disabled={semChaveDeEmbedding}>Adicionar documento</Button>}
                    />
                ) : (
                    <DataTable columns={colunas} rows={documentos} rowKey="id" />
                )}
            </Card>

            <Modal
                open={aberto} onClose={fechar} title="Adicionar à base" width={560} dirty={formSujo}
                footer={
                    <>
                        <Button variant="secondary" onClick={fechar}>Cancelar</Button>
                        <Button onClick={() => { void salvar(); }} loading={salvando}>Adicionar</Button>
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                    <FormField label="Título" required hint="É por ele que a resposta cita a fonte.">
                        <Input value={titulo} onChange={setTitulo} placeholder="Política de remarcação" aria-label="Título" />
                    </FormField>

                    <FormField
                        label="Conteúdo"
                        hint="Cole o texto aqui, ou envie um arquivo abaixo. PDF digitalizado é imagem e não tem texto para ler."
                    >
                        <Textarea
                            value={texto} onChange={setTexto} rows={7}
                            disabled={!!arquivo}
                            placeholder="O paciente pode reagendar sem custo avisando com…"
                            aria-label="Conteúdo"
                        />
                    </FormField>

                    <div>
                        <input
                            id={idArquivo} type="file"
                            accept="application/pdf,text/plain,text/markdown,text/csv"
                            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                            style={{ display: 'none' }}
                        />
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Button
                                size="sm" variant="secondary" iconLeft={<Icon name="paperclip" size={14} />}
                                onClick={() => document.getElementById(idArquivo)?.click()}
                            >
                                {arquivo ? 'Trocar arquivo' : 'Enviar arquivo'}
                            </Button>
                            {arquivo && (
                                <>
                                    <span style={{ fontSize: 12.5 }}>{arquivo.name}</span>
                                    <IconButton
                                        size="sm" icon={<Icon name="x" size={13} />} label="Remover arquivo"
                                        onClick={() => setArquivo(null)}
                                    />
                                </>
                            )}
                        </span>
                    </div>
                </div>
            </Modal>

            <Modal
                open={!!removendo} onClose={() => setRemovendo(null)} tone="danger"
                title={removendo ? `Remover ${removendo.titulo}?` : 'Remover'}
                width={440}
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setRemovendo(null)}>Cancelar</Button>
                        <Button variant="danger" onClick={() => { void remover(); }}>Remover</Button>
                    </>
                }
            >
                <p style={{ margin: 0, fontSize: 13.5 }}>
                    O assistente deixa de consultar este documento. O arquivo enviado sai junto — guardar o
                    texto que a clínica mandou apagar, num lugar que a tela não mostra mais, seria guardar
                    escondido.
                </p>
            </Modal>
        </div>
    );
}
